import { useState } from "react";
import { Building2, Loader2, CheckCircle, XCircle, AlertTriangle, Copy, Globe, Mail, Shield, Server, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TenantResult {
  domain: string;
  tenant: {
    tenantId: string | null;
    onMicrosoftDomain: string | null;
    isM365Tenant: boolean;
    domains: string[];
  };
  google: {
    isGoogleWorkspace: boolean;
    customerId: string | null;
    verificationRecords: string[];
  };
  emailProvider: string;
  dns: {
    mx: {
      records: string[];
      isM365: boolean;
      isGoogleWorkspace: boolean;
    };
    spf: {
      record: string | null;
      includesM365: boolean;
      includesGoogle: boolean;
    };
    dmarc: string | null;
    autodiscover: {
      record: string | null;
      isM365: boolean;
      isOnPremise: boolean;
    };
    dkim: { selector: string; record: string }[];
  };
}

export const M365TenantAnalyzer = () => {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TenantResult | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const analyzeDomain = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("m365-tenant-lookup", {
        body: { domain: domain.trim() },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to analyze domain");
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status, label }: { status: "success" | "warning" | "error"; label: string }) => {
    const colors = {
      success: "bg-green-500/10 text-green-400 border-green-500/30",
      warning: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      error: "bg-red-500/10 text-red-400 border-red-500/30",
    };
    const icons = {
      success: <CheckCircle className="w-3 h-3" />,
      warning: <AlertTriangle className="w-3 h-3" />,
      error: <XCircle className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colors[status]}`}>
        {icons[status]}
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Domain to Analyze
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && analyzeDomain()}
              className="flex-1"
            />
            <Button onClick={analyzeDomain} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing
                </>
              ) : (
                "Check"
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Analyze Google Workspace & Microsoft 365 tenant info, all domains, and DNS records
          </p>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Tenant Info Card */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Email Tenant Info</h3>
                  <p className="text-sm text-muted-foreground">{result.domain}</p>
                </div>
              </div>
              {result.tenant.isM365Tenant ? (
                <StatusBadge status="success" label="Microsoft 365" />
              ) : result.google.isGoogleWorkspace ? (
                <StatusBadge status="success" label="Google Workspace" />
              ) : (
                <StatusBadge status="warning" label="No Tenant Found" />
              )}
            </div>

            {result.tenant.tenantId && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tenant ID</span>
                  <button
                    onClick={() => copyToClipboard(result.tenant.tenantId!, "Tenant ID")}
                    className="flex items-center gap-2 text-sm font-mono text-foreground hover:text-primary transition-colors"
                  >
                    {result.tenant.tenantId}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Tenant Domains */}
            {result.tenant.domains.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Domains on this Tenant</span>
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                    {result.tenant.domains.length} found
                  </span>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {result.tenant.domains.map((domain, i) => (
                      <div key={i} className="flex items-center justify-between group">
                        <span className="text-sm font-mono text-foreground">{domain}</span>
                        <button
                          onClick={() => copyToClipboard(domain, "Domain")}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Copy className="w-3 h-3 text-muted-foreground hover:text-primary" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Google Workspace Info */}
          {result.google.isGoogleWorkspace && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <Mail className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Google Workspace</h3>
                    <p className="text-sm text-muted-foreground">Detected via DNS records</p>
                  </div>
                </div>
                <StatusBadge status="success" label="Google Workspace" />
              </div>

              {result.google.verificationRecords.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium text-foreground">Verification Records</span>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                    {result.google.verificationRecords.map((record, i) => (
                      <p key={i} className="text-xs font-mono text-muted-foreground break-all">
                        {record}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email Provider */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Email Provider</h3>
                <p className="text-sm text-muted-foreground">{result.emailProvider}</p>
              </div>
            </div>
          </div>

          {/* DNS Records */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-foreground">DNS Records</h3>
            </div>

            <div className="space-y-4">
              {/* MX Records */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" /> MX Records
                  </span>
                  {result.dns.mx.isM365 ? (
                    <StatusBadge status="success" label="Microsoft 365" />
                  ) : result.dns.mx.isGoogleWorkspace ? (
                    <StatusBadge status="warning" label="Google Workspace" />
                  ) : result.dns.mx.records.length > 0 ? (
                    <StatusBadge status="warning" label="Other Provider" />
                  ) : (
                    <StatusBadge status="error" label="No MX Records" />
                  )}
                </div>
                {result.dns.mx.records.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    {result.dns.mx.records.map((mx, i) => (
                      <p key={i} className="text-xs font-mono text-muted-foreground break-all">
                        {mx}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Autodiscover */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Server className="w-4 h-4" /> Autodiscover
                  </span>
                  {result.dns.autodiscover.isM365 ? (
                    <StatusBadge status="success" label="Cloud (O365)" />
                  ) : result.dns.autodiscover.isOnPremise ? (
                    <StatusBadge status="warning" label="On-Premise" />
                  ) : (
                    <StatusBadge status="error" label="Not Configured" />
                  )}
                </div>
                {result.dns.autodiscover.record && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs font-mono text-muted-foreground break-all">
                      {result.dns.autodiscover.record}
                    </p>
                  </div>
                )}
              </div>

              {/* SPF */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" /> SPF Record
                  </span>
                  <div className="flex items-center gap-2">
                    {result.dns.spf.record ? (
                      <>
                        {result.dns.spf.includesM365 && (
                          <StatusBadge status="success" label="M365" />
                        )}
                        {result.dns.spf.includesGoogle && (
                          <StatusBadge status="success" label="Google" />
                        )}
                        {!result.dns.spf.includesM365 && !result.dns.spf.includesGoogle && (
                          <StatusBadge status="warning" label="Present" />
                        )}
                      </>
                    ) : (
                      <StatusBadge status="error" label="Missing" />
                    )}
                  </div>
                </div>
                {result.dns.spf.record && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs font-mono text-muted-foreground break-all">
                      {result.dns.spf.record}
                    </p>
                  </div>
                )}
              </div>

              {/* DMARC */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" /> DMARC Record
                  </span>
                  {result.dns.dmarc ? (
                    <StatusBadge status="success" label="Configured" />
                  ) : (
                    <StatusBadge status="error" label="Missing" />
                  )}
                </div>
                {result.dns.dmarc && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs font-mono text-muted-foreground break-all">
                      {result.dns.dmarc}
                    </p>
                  </div>
                )}
              </div>

              {/* DKIM */}
              {result.dns.dkim.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Key className="w-4 h-4" /> DKIM Selectors
                  </span>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    {result.dns.dkim.map((dkim, i) => (
                      <div key={i} className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{dkim.selector}:</span>
                        </p>
                        <p className="text-xs font-mono text-muted-foreground break-all pl-2">
                          {dkim.record}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};
